//* Import necessary modules
import { useQuery } from "@tanstack/react-query";

//* Import API
import { permissionAPI } from "../../hooks/permissions/permissionAPI";

//? Import types
import type {
  PermissionDetail,
  GetUserPermissionsResponse,
} from "../../hooks/permissions/types/types";


/**
 * Custom hook to fetch and manage user permissions.
 *
 * @param {string} token - The authentication token.
 * @param {string} userId - The ID of the user whose permissions are being fetched.
 * @returns {Object} An object containing:
 * - `permissions` {PermissionDetail[]} - The list of user permissions.
 * - `loading` {boolean} - Indicates if the permissions are currently being loaded.
 * - `error` {string | null} - Error message if there was an error fetching permissions, otherwise null.
 * - `refetch` {Function} - Function to manually refetch the permissions.
 */
export const useUserPermissions = (token: string, userId: string) => {
  const { data, isLoading, isError, error, refetch } = useQuery<
    GetUserPermissionsResponse,
    Error
  >({
    queryKey: ["userPermissions", userId],
    queryFn: () => permissionAPI.getUserPermissions(userId, token),
    enabled: !!userId, // Executa apenas se userId estiver definido
    staleTime: 5 * 60 * 1000, // 5 minutos
  });


  const permissions: PermissionDetail[] = data?.data.permissions || [];


  return {
    permissions,
    loading: isLoading,
    error: isError ? error.message : null,
    refetch,
  };
};
